/**
 * QueueFilters Component
 * 
 * Filter controls for the Hot Lead Queue.
 * Allows filtering by state, opportunity type, score range, etc.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Flex,
  Select,
  Input,
  Button,
  IconButton,
  HStack,
  VStack,
  Text,
  Badge,
  Collapse,
  useDisclosure,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import { 
  FiFilter, 
  FiX, 
  FiChevronDown, 
  FiChevronUp,
  FiRefreshCw,
} from 'react-icons/fi';

// US States for filtering
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

// Opportunity types from the CSV data
const OPPORTUNITY_TYPES = [
  { value: '', label: 'All Opportunities' },
  { value: 'VA_IRRRL', label: 'VA IRRRL' },
  { value: 'RATE_REDUCTION', label: 'Rate Reduction' },
  { value: 'FHA_STREAMLINE', label: 'FHA Streamline' },
  { value: 'PMI_REMOVAL', label: 'PMI Removal' },
  { value: 'CASH_OUT', label: 'Cash Out' },
  { value: 'ARM_CONVERSION', label: 'ARM Conversion' },
  { value: 'TERM_REDUCTION', label: 'Term Reduction' },
  { value: 'DTI_REDUCTION', label: 'DTI Reduction' },
];

// Mortgage types
const MORTGAGE_TYPES = [
  { value: '', label: 'All Loan Types' },
  { value: 'VA', label: 'VA' },
  { value: 'Conventional', label: 'Conventional' },
  { value: 'FHA', label: 'FHA' },
  { value: 'USDA', label: 'USDA' },
  { value: 'Jumbo', label: 'Jumbo' },
];

export function QueueFilters({ filters, onFilterChange, onRefresh, loading }) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false });
  const [localFilters, setLocalFilters] = useState(filters || {});
  const [scoreRange, setScoreRange] = useState([
    filters?.minScore ?? 0, 
    filters?.maxScore ?? 100
  ]);

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bgColor = useColorModeValue('white', 'gray.800');

  // Count active filters
  const activeFilterCount = Object.entries(localFilters).filter(
    ([key, value]) => value && value !== '' && key !== 'minScore' && key !== 'maxScore'
  ).length + (scoreRange[0] > 0 || scoreRange[1] < 100 ? 1 : 0);

  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
  }, [localFilters]);

  // Handle score range change
  const handleScoreRangeChange = useCallback((values) => {
    setScoreRange(values);
  }, []);

  // Apply score range on drag end
  const handleScoreRangeEnd = useCallback((values) => {
    setScoreRange(values);
    handleFilterChange('minScore', values[0] > 0 ? values[0] : undefined);
    handleFilterChange('maxScore', values[1] < 100 ? values[1] : undefined);
  }, [handleFilterChange]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    const clearedFilters = {};
    setLocalFilters(clearedFilters);
    setScoreRange([0, 100]);
    onFilterChange(clearedFilters);
  }, [onFilterChange]);

  // Apply filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onFilterChange(localFilters);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [localFilters, onFilterChange]);

  return (
    <Box 
      mb={4} 
      p={4} 
      bg={bgColor} 
      borderRadius="lg" 
      borderWidth="1px" 
      borderColor={borderColor}
    >
      {/* Filter Header */}
      <Flex justify="space-between" align="center" mb={isOpen ? 4 : 0}>
        <HStack spacing={3}>
          <Button
            size="sm"
            leftIcon={<FiFilter />}
            rightIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
            onClick={onToggle}
            variant="ghost"
          >
            Filters
          </Button>
          {activeFilterCount > 0 && (
            <Badge colorScheme="blue" borderRadius="full" px={2}>
              {activeFilterCount} active
            </Badge>
          )}
        </HStack>

        <HStack spacing={2}>
          {activeFilterCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FiX />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
          <Tooltip label="Refresh queue">
            <IconButton
              size="sm"
              icon={<FiRefreshCw />}
              onClick={onRefresh}
              isLoading={loading}
              aria-label="Refresh queue"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Expandable Filter Options */}
      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={4} align="stretch">
          {/* Row 1: Primary Filters */}
          <Flex gap={4} flexWrap="wrap">
            {/* State Filter */}
            <Box flex="1" minW="150px">
              <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.500">
                State
              </Text>
              <Select
                size="sm"
                placeholder="All States"
                value={localFilters.state || ''}
                onChange={(e) => handleFilterChange('state', e.target.value)}
              >
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Select>
            </Box>

            {/* Opportunity Type */}
            <Box flex="1.5" minW="200px">
              <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.500">
                Opportunity Type
              </Text>
              <Select
                size="sm"
                value={localFilters.opportunityType || ''}
                onChange={(e) => handleFilterChange('opportunityType', e.target.value)}
              >
                {OPPORTUNITY_TYPES.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </Box>

            {/* Mortgage Type */}
            <Box flex="1" minW="150px">
              <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.500">
                Loan Type
              </Text>
              <Select
                size="sm"
                value={localFilters.mortgageType || ''}
                onChange={(e) => handleFilterChange('mortgageType', e.target.value)}
              >
                {MORTGAGE_TYPES.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </Box>
          </Flex>

          {/* Row 2: Score Range */}
          <Box>
            <Flex justify="space-between" mb={2}>
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Deal Score Range
              </Text>
              <Text fontSize="xs" color="gray.600">
                {scoreRange[0]} - {scoreRange[1]}
              </Text>
            </Flex>
            <RangeSlider
              min={0}
              max={100}
              step={5}
              value={scoreRange}
              onChange={handleScoreRangeChange}
              onChangeEnd={handleScoreRangeEnd}
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack bg="blue.400" />
              </RangeSliderTrack>
              <RangeSliderThumb index={0} boxSize={4}>
                <Box color="blue.500" />
              </RangeSliderThumb>
              <RangeSliderThumb index={1} boxSize={4}>
                <Box color="blue.500" />
              </RangeSliderThumb>
            </RangeSlider>
            <Flex justify="space-between" mt={1}>
              <Text fontSize="xs" color="gray.400">0 (Low)</Text>
              <Text fontSize="xs" color="gray.400">100 (High)</Text>
            </Flex>
          </Box>
        </VStack>
      </Collapse>
    </Box>
  );
}

export default QueueFilters;
